// GlobalCallBanner.js
// ─────────────────────────────────────────────────────────────────────────
// The always-on-top banner for the app-wide voice call. It is rendered ONCE
// at the App root (next to <Routes>), so it stays mounted and visible while
// the user navigates between screens — the agent keeps talking, the banner
// keeps showing.
//
// Android/iOS ONLY. Renders nothing on web or when no call is live.
// ─────────────────────────────────────────────────────────────────────────
import React, { useState, useRef, useEffect } from "react";
import { useLocation } from "react-router-dom";
import styles from "./GlobalCallBanner.module.css";
import { useGlobalCall, isMobileAppSurface } from "../../contexts/GlobalCallContext";
import { isPythonBackend } from "../../utils/apiBackend";
import KeepTalkingModal from "./KeepTalkingModal";
import CallWaveform from "./CallWaveform";
import { useOfferStore } from "../../Store/offerStore";
import { useAgentStore } from "../../Store/agentDetailStore";

const DEFAULT_AVATAR = "/svg/receptionist_profile.png";

const parseVoipNumber = (raw) => {
  if (!raw) return "";
  if (Array.isArray(raw)) return raw[0] || "";
  if (typeof raw === "string") {
    if (raw.trim().startsWith("[")) {
      try {
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed[0] || "" : parsed || "";
      } catch {
        return raw;
      }
    }
    return raw;
  }
  return "";
};

const hasAssignedNumber = (agent) => {
  if (!agent) return false;
  return Boolean(parseVoipNumber(agent.voip_numbers));
};

// The call runs app-wide, but the banner is only wanted on a few screens:
//   • /fast-step        → sirf tab jab agent ke paas number NA ho. Us haalat me
//                          FastSetup khud banner ko prime karta hai aur wahi
//                          call shuru karne ka ekmatra rasta hai (uska
//                          handleTestSophiaAgain kahin render nahi hota).
//                          Number aa jaane ke baad banner ki "X is ready for Y"
//                          line screen ke apne header ki hu-ba-hu nakal hai —
//                          sirf duplicate dikhta hai, kaam kuch nahi aata.
//                          Dekho SCREEN_NEEDS_NUMBERLESS_BANNER.
//   • /recommended-plan → the purple banner IS the call UI here (variant A).
//   • /receptionist-number → variant B ka number screen; wahan bhi call chalti
//                          rehti hai aur banner hi uska UI hai.
// /agent-live (variant B ki AAKHRI screen) JAAN-BOOJH KE bahar hai: wahan user
// ko apne phone se "Direct Dial Now" karna hota hai, to web call ka banner
// dhyan batata hai.
// On every OTHER route (fast-agent-detail, dashboard, …) the banner is hidden
// even while the call is still running in the background.
const BANNER_ALLOWED_ROUTES = [
  "/fast-step",
  "/recommended-plan",
  "/receptionist-number",
];
const BANNER_HIDDEN_ROUTES = [
  "/21-day-trial",
  "/call-summary",
  "/setup-path",
];
// Yahan banner SIRF un users ke liye hai jinke paas abhi number nahi hai —
// unke liye ye call shuru karne ka ekmatra button hai. Number mil jaane ke baad
// ye screen ke apne "X is ready for Y" header ka duplicate ban jaata hai.
const SCREEN_NEEDS_NUMBERLESS_BANNER = ["/fast-step"];

const isRouteMatch = (pathname, routes) =>
  routes.some((r) => pathname === r || pathname.startsWith(`${r}/`));

const GlobalCallBanner = () => {
  const { callStatus, isAgentTalking, agentMeta, endGlobalCall, startGlobalCall, injectContext } = useGlobalCall();
  const location = useLocation();
  const { agentData, getAgentById } = useAgentStore();
  const agentId = agentMeta?.agentId || (typeof sessionStorage !== "undefined" && (sessionStorage.getItem("SelectAgentId") || sessionStorage.getItem("agentId"))) || null;
  const currentAgent = agentData?.agent || (agentId ? getAgentById(agentId)?.agentData?.agent : null);
  const agentHasVoip = hasAssignedNumber(currentAgent);
  // "Can we please keep talking?" confirmation before ending.
  const [showEndConfirm, setShowEndConfirm] = useState(false);

  // Populate the agent's prior call count in the offer store whenever the banner
  // is active — the banner persists across screens (e.g. /recommended-plan), so
  // this covers reloads there where /fast-step never mounts. count > 0 means the
  // owner has tested before → offer pay buttons stay visible even mid-call.
  const setPriorCallCount = useOfferStore((s) => s.setPriorCallCount);
  useEffect(() => {
    const id = agentMeta?.agentId || sessionStorage.getItem("SelectAgentId");
    if (!id) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/agent/call-count/${id}`
        );
        const data = await res.json();
        if (!cancelled && typeof data?.count === "number") {
          setPriorCallCount(data.count);
        }
      } catch (_e) {
        /* ignore */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [agentMeta?.agentId, setPriorCallCount]);

  // Entrance zoom plays ONLY when the banner first appears for a call (the
  // idle → live transition) — NOT every time a route toggles it back on. We
  // detect the transition here and flip on the intro class for one animation.
  const [showIntro, setShowIntro] = useState(false);
  const prevActiveRef = useRef(false);
  useEffect(() => {
    const isActive = callStatus !== "idle";
    if (isActive && !prevActiveRef.current) {
      setShowIntro(true);
      const t = setTimeout(() => setShowIntro(false), 1100);
      prevActiveRef.current = isActive;
      return () => clearTimeout(t);
    }
    prevActiveRef.current = isActive;
    return undefined;
  }, [callStatus]);

  // Nothing to show when idle.
  if (callStatus === "idle") return null;
  // Banner only lives on the two allowed screens; hidden everywhere else even
  // while the call is still running in the background.
  if (!isRouteMatch(location.pathname, BANNER_ALLOWED_ROUTES)) return null;
  // Hidden on hidden routes if any configured.
  if (BANNER_HIDDEN_ROUTES.length > 0 && isRouteMatch(location.pathname, BANNER_HIDDEN_ROUTES)) return null;
  // /fast-step: number aa chuka hai to banner sirf duplicate hai — screen apna
  // "X is ready for Y" header khud dikhati hai. Number na hone par banner rehta
  // hai, kyunki wahan wahi ek call button hai.
  if (isRouteMatch(location.pathname, SCREEN_NEEDS_NUMBERLESS_BANNER) && agentHasVoip) {
    return null;
  }
  // The upgrade flow is NOT a live sales call — it's reached from the
  // dashboard/agent-detail "Upgrade Agent" action — so the call banner must not
  // appear there. Two shapes reach the same screen:
  //   • ?mode=plans  → full plans list (any variant, existing paid plan)
  //   • ?from=upgrade → variant B ka recommended screen (number pehle se hai,
  //                     to mode=plans nahi lagta — isliye alag marker chahiye)
  //
  // agentHasVoip: agent ke paas number PEHLE SE hai to ye screen ek pure
  // PAYMENT screen hai, live sales call nahi — banner nahi aana chahiye.
  //
  // Ye check pehle `isVariantB() && agentHasVoip` tha. Wo galat tha: variant
  // ek ROLLOUT switch hai, screen ki haalat nahi. EXPERIMENT_GO_LIVE_FLOW=A
  // set karte hi isVariantB() false ho jaata aur wahi B-flow user — jiske paas
  // number already tha — $5 screen pe purple banner dekhne lagta. Number hai ya
  // nahi, bas isi se faisla hona chahiye; variant se koi lena-dena nahi.
  //
  // Variant A pe koi asar nahi: wahan number payment ke BAAD wizard me milta
  // hai, to is screen pe agentHasVoip false hota hai aur banner pehle jaisa
  // hi dikhta hai.
  if (location.pathname === "/recommended-plan") {
    const params = new URLSearchParams(location.search);
    const currentPlan = typeof sessionStorage !== "undefined" ? sessionStorage.getItem("currentAgentPlan") : null;
    const hasPaidUpgradePlan = Boolean(currentPlan && currentPlan !== "free" && currentPlan !== "demo" && currentPlan !== "");

    const isUpgradeSession =
      typeof sessionStorage !== "undefined" &&
      (Boolean(sessionStorage.getItem("upgradeReturnTo")) || hasPaidUpgradePlan);

    if (
      params.get("mode") === "plans" ||
      params.get("from") === "upgrade" ||
      isUpgradeSession ||
      agentHasVoip
    ) {
      return null;
    }
  }

  const agentName = agentMeta?.agentName || sessionStorage.getItem("agentName") || "Sophia";
  const businessName = agentMeta?.businessName || sessionStorage.getItem("businessName") || "";
  const avatar = agentMeta?.agentAvatar || DEFAULT_AVATAR;
  const callEnded = callStatus === "ended";
  // "ready" = banner primed hai par call shuru nahi hui (dashboard ke "Test
  // Agent Call" se aaye hain). Call band hai, to button green "start" hona
  // chahiye — red "end" nahi, warna dabane par kuch hota hi nahi.
  const canStartCall = callStatus === "ended" || callStatus === "ready";
  // Show the voice-reactive wave ONLY while the call is actually live. Before it
  // starts (connecting/ready) or after it ends, there's no audio — keep it hidden.
  const showWave = callStatus === "active" || callStatus === "ending";

  // Fallback jab business name hi na mile. "ended" ka apna label zaroori hai —
  // warna call khatam hone ke baad bhi "Listening…" dikhta.
  const statusLabel =
    callStatus === "connecting"
      ? "Connecting…"
      : callStatus === "ending"
      ? "Ending…"
      : callStatus === "ended"
      ? "Call ended"
      : callStatus === "ready"
      ? "Tap to call"
      : isAgentTalking
      ? "Speaking…"
      : "Listening…";

  return (
    <div className={`${styles.banner} ${showIntro ? styles.bannerIntro : ""}`} role="status" aria-live="polite">
      <div
        className={`${styles.avatarWrap} ${
          isAgentTalking ? styles.avatarTalking : ""
        }`}
      >
        <img
          src={avatar}
          alt={agentName}
          className={styles.avatar}
          onError={(e) => {
            // Agent ka custom avatar 404 kare to generic tasveer pe gir jao,
            // warna banner me toota hua image icon dikhta hai.
            e.currentTarget.src = DEFAULT_AVATAR;
          }}
        />
        <span className={styles.liveDot} aria-hidden="true" ><img src="/svg/verified 1.svg" alt="Live" /></span>
      </div>

      <div className={styles.info}>
        <span className={styles.name}>
          {agentName} <span className={styles.ready}>is ready</span>
        </span>
        {/* Business name HAR state me dikhta hai — call se pehle, call ke
            dauraan, aur call khatam hone ke baad bhi. Pehle "ended" par yahan
            khaali span render hota tha, to call cut hote hi "for <business>"
            gayab ho jata tha. Naam call ki property nahi hai, agent ki hai. */}
        {businessName ? (
          <span className={styles.status}>
            for <span className={styles.biz}>{businessName}</span>
          </span>
        ) : (
          <span className={styles.status}>{statusLabel}</span>
        )}
      </div>

      {/* Real, voice-reactive waveform driven by the live call's audio — only
          rendered while the call is live (hidden before start / after end). */}
      {showWave && (
        <CallWaveform bars={8} maxHeight={22} minHeight={4} barWidth={3} gap={3} />
      )}

      {/* Red End Call button → asks "keep talking?" before ending. */}
      <button
        type="button"
        className={`${styles.endBtn} ${canStartCall ? styles.endBtnEnded : ""}`}
        onClick={() => {
          if (canStartCall) {
            startGlobalCall(agentMeta || {});
            return;
          }
          // [FIX: python-call-hangup-oneclick] Python flow: injectContext +
          // keep-talking modal ka koi matlab nahi (agent live-context le hi nahi
          // sakta), aur usse call pehle click me cut nahi hoti thi. Node jaisa
          // one-click end — seedha endGlobalCall (jo Python WS hangUp karta hai).
          if (isPythonBackend()) {
            endGlobalCall();
            return;
          }
          // Retention: ask the live agent (via update-live) to find out WHY the
          // caller is ending / what went wrong, before the keep-talking modal.
          injectContext(
            "CONTEXT: The customer just tapped the End Call button. Do NOT end the call yourself and do NOT say goodbye. In ONE short, warm, empathetic sentence, ask them why they'd like to end the call and whether something went wrong, so you can help. Keep it brief and never pushy."
          );
          setShowEndConfirm(true);
        }}
        aria-label={
          callStatus === "ready" ? "Start call" : callEnded ? "Call again" : "End call"
        }
      >
        <img src="/svg/phone_cut_icon2.svg" alt="" aria-hidden="true" />
      </button>

      <KeepTalkingModal
        isOpen={showEndConfirm}
        onKeepTalking={() => setShowEndConfirm(false)}
        onEndCall={() => {
          setShowEndConfirm(false);
          endGlobalCall();
        }}
      />
    </div>
  );
};

export default GlobalCallBanner;
